import {
  ArrowRight,
  BadgeCheck,
  BriefcaseBusiness,
  Check,
  ChevronRight,
  CircleAlert,
  Coins,
  CreditCard,
  Eye,
  FileCheck2,
  FileText,
  FolderOpen,
  Gauge,
  KeyRound,
  Layers3,
  LockKeyhole,
  LogOut,
  Mail,
  Menu,
  Plus,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Trash2,
  TrendingUp,
  Upload,
  UserRound,
  WandSparkles,
  X
} from "lucide-react";
import {
  FormEvent,
  ReactNode,
  useCallback,
  useEffect,
  useState
} from "react";
import { ApiError, api, apiFileUrl, newIdempotencyKey } from "./api";
import { Link, Navigate, NavLink, useNavigate, usePath } from "./router";

type Me = {
  id: string;
  email: string;
  emailConfirmed: boolean;
  freeReviewsRemaining: number;
  tokenBalance: number;
  walletActivated: boolean;
  isAdmin: boolean;
};

type ReviewSummary = {
  id: string;
  mode: string;
  status: string;
  aggregateScore: number | null;
  minimumScore: number | null;
  maximumScore: number | null;
  resumeTitle: string;
  vacancyTitle: string;
  turnkeyStage?: string;
  baselineScore?: number | null;
  verificationScore?: number | null;
  scoreGrowth?: number | null;
  successfulModelCount: number;
  requestedModelCount: number;
  createdAt: string;
};

type CriterionScore = {
  key: string;
  score: number;
  explanation?: string;
};

type Evaluation = {
  id: string;
  modelId: string;
  displayName: string;
  provider?: string;
  phase: "Initial" | "Verification";
  status: string;
  totalScore: number | null;
  criteria?: CriterionScore[] | Record<string, number | CriterionScore>;
  evidence?: string[];
  gaps?: string[];
  recommendations?: string[] | null;
  isExtraModel: boolean;
  tokenPrice: number;
  failureReason?: string;
  latencyMs?: number;
};

type Review = ReviewSummary & {
  resume: DocumentPreview;
  vacancy: DocumentPreview;
  recommendationsAccess: string;
  reservedTokens: number;
  capturedTokens: number;
  finalPriceTokens: number;
  rewriteComparison?: RewriteComparison | null;
  requiredGrowth?: number | null;
  rewrites: RewriteSummary[];
  evaluations: Evaluation[];
};
type RewriteComparison = {
  sourceReviewId: string;
  beforeScore: number | null;
  afterScore: number | null;
  delta: number | null;
  models: Array<{
    modelId: string;
    displayName: string;
    beforeScore: number | null;
    afterScore: number;
    delta: number | null;
  }>;
};


type RewriteSummary = {
  id: string;
  version: number;
  isTurnkey?: boolean;
  verifyAfterRewrite?: boolean;
  verificationReviewId?: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
};

type RewriteDetails = RewriteSummary & {
  content?: string;
  questions?: string[];
  failureReason?: string;
  docxUrl?: string;
};

type DocumentResult = {
  id: string;
  kind: string;
  sourceType: string;
  status: string;
  title?: string;
  extractedText?: string;
  replacements?: Record<string, string>;
  failureReason?: string;
  requiresShortening?: boolean;
  reused?: boolean;
};

type DocumentPreview = {
  title: string;
  preview: string;
};

type SavedDocument = DocumentPreview & {
  id: string;
  kind: "Resume" | "Vacancy";
  sourceType: string;
  originalFileName?: string;
  sourceUrl?: string;
  createdAt: string;
  updatedAt: string;
};

type OptimizationSummary = {
  id: string;
  reviewId: string;
  version: number;
  isTurnkey: boolean;
  verifyAfterRewrite: boolean;
  status: string;
  resumeTitle: string;
  vacancyTitle: string;
  vacancyDocumentId: string;
  createdAt: string;
  updatedAt: string;
};

type HhStatus = {
  available: boolean;
  mode: "Live" | "Mock";
  connected: boolean;
  externalUserId?: string;
};

type HhResume = {
  id: string;
  title: string;
  updatedAt?: string;
};

type ReviewSource = {
  kind: "Resume" | "Vacancy";
  title: string;
  text: string;
};

type ExtraModel = {
  modelId: string;
  displayName: string;
  family: string;
  tokenPrice: number;
  contextLength: number;
};

const criteriaLabels: Record<string, string> = {
  mandatoryRequirements: "Обязательные требования",
  relevantExperience: "Релевантный опыт",
  skillsAndKeywords: "Навыки и ключевые слова",
  measurableAchievements: "Доказанные достижения",
  seniorityScopeDomain: "Уровень, масштаб и домен",
  clarityAndReadability: "Ясность и машиночитаемость"
};

const criteriaMaxima: Record<string, number> = {
  mandatoryRequirements: 30,
  relevantExperience: 20,
  skillsAndKeywords: 20,
  measurableAchievements: 15,
  seniorityScopeDomain: 10,
  clarityAndReadability: 5
};

function App() {
  const path = usePath();
  const [me, setMe] = useState<Me | null>(null);
  const [authChecked, setAuthChecked] = useState(false);

  const refreshMe = useCallback(async () => {
    try {
      setMe(await api<Me>("/api/v1/auth/me"));
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) setMe(null);
      else throw error;
    } finally {
      setAuthChecked(true);
    }
  }, []);

  useEffect(() => {
    void refreshMe();
  }, [refreshMe]);

  if (path === "/") return <Landing me={me} />;
  if (path === "/privacy") return <Privacy />;
  if (path === "/auth") return <AuthPage me={me} onAuthenticated={refreshMe} />;
  if (!path.startsWith("/app")) return <Navigate to="/" replace />;

  let page: ReactNode;
  if (path === "/app") page = <OverviewPage me={me!} />;
  else if (path === "/app/new") page = <NewReview onChanged={refreshMe} />;
  else if (path === "/app/history") page = <Navigate to="/app" replace />;
  else if (path === "/app/profile") page = <ProfilePage me={me!} />;
  else if (path === "/app/profile/email") page = <ProfileConfirmationPage kind="email" onChanged={refreshMe} />;
  else if (path === "/app/profile/delete") page = <ProfileConfirmationPage kind="delete" onChanged={refreshMe} />;
  else if (/^\/app\/reviews\/[0-9a-f-]+$/i.test(path)) {
    page = <ReviewPage onChanged={refreshMe} />;
  } else {
    page = <Navigate to="/app" replace />;
  }

  return (
    <Protected me={me} checked={authChecked}>
      <AppShell me={me!} onRefresh={refreshMe}>{page}</AppShell>
    </Protected>
  );
}

function Protected({
  me,
  checked,
  children
}: {
  me: Me | null;
  checked: boolean;
  children: ReactNode;
}) {
  if (!checked) return <FullScreenLoader />;
  return me ? children : <Navigate to="/auth" replace />;
}

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <Link className="brand" to="/">
      <span className="brand-mark">CV</span>
      {!compact && <span>Reviewer</span>}
    </Link>
  );
}

function Landing({ me }: { me: Me | null }) {
  const [menu, setMenu] = useState(false);
  return (
    <div className="landing">
      <header className="site-header">
        <Brand />
        <nav className={menu ? "public-nav is-open" : "public-nav"}>
          <a href="#why">Зачем несколько AI</a>
          <a href="#how">Как работает</a>
          <a href="#models">Модели</a>
          <a href="#pricing">Тарифы</a>
          <a href="#faq">FAQ</a>
        </nav>
        <div className="header-actions">
          <Link className="button ghost desktop-only" to={me ? "/app" : "/auth"}>
            {me ? "В кабинет" : "Войти"}
          </Link>
          <Link className="button ghost mobile-only mobile-login" to={me ? "/app" : "/auth"}>
            {me ? "Кабинет" : "Войти"}
          </Link>
          <Link className="button primary" to={me ? "/app/new" : "/auth"}>
            Проверить резюме <ArrowRight size={17} />
          </Link>
          <button className="icon-button mobile-only" onClick={() => setMenu(!menu)} aria-label="Меню">
            {menu ? <X /> : <Menu />}
          </button>
        </div>
      </header>

      <main>
        <section className="hero">
          <div className="hero-copy">
            <div className="eyebrow"><Sparkles size={15} /> Пять AI-ревьюеров бесплатно</div>
            <h1>Не угадывайте, какая нейросеть будет <em>первой читать резюме</em></h1>
            <p className="hero-lead">
              При большом потоке откликов первым ревьюером может стать LLM. Но DeepSeek,
              OpenAI, Qwen, GigaChat и Alice оценивают одну и ту же пару по-разному.
              CVReviewer показывает, где конкретная модель видит сильное соответствие,
              а где может занизить оценку. Цель — сделать резюме понятным как можно
              большему числу AI-ревьюеров и повысить шанс дойти до собеседования.
            </p>
            <div className="hero-actions">
              <Link className="button primary large" to={me ? "/app/new" : "/auth"}>
                Проверить у 5 моделей бесплатно <ArrowRight size={19} />
              </Link>
              <a className="text-link" href="#why">Почему одна модель — риск</a>
            </div>
            <div className="trust-row">
              <span><BadgeCheck size={18} /> 5 проверок навсегда</span>
              <span><Layers3 size={18} /> До 10 AI-ревьюеров</span>
              <span><WandSparkles size={18} /> Автоприменение правок</span>
              <span><ShieldCheck size={18} /> Контакты обезличиваются</span>
            </div>
          </div>
          <ScorePreview />
        </section>

        <section className="model-strip" id="models">
          <p>Одна пара «резюме + вакансия» — отдельная независимая проверка каждой моделью</p>
          <div>
            {["DeepSeek", "OpenAI", "Claude", "Qwen", "Gemini", "GigaChat", "Alice AI"].map(
              (model) => <span key={model}>{model}</span>
            )}
          </div>
        </section>

        <section className="evidence-section" id="why">
          <div className="evidence-heading">
            <span>Почему это важно</span>
            <h2>Одна вакансия. Одно резюме. <em>Разные вердикты.</em></h2>
            <p>
              Вы не знаете, какой моделью пользуется конкретный рекрутер. Поэтому проверка
              только в знакомом вам чате оставляет слепую зону.
            </p>
          </div>
          <div className="evidence-grid">
            <article>
              <span>01</span>
              <h3>LLM может быть первым фильтром</h3>
              <p>
                При сотнях откликов рекрутер может поручить нейросети первичное сравнение
                резюме с требованиями вакансии.
              </p>
            </article>
            <article>
              <span>02</span>
              <h3>У моделей разные предпочтения</h3>
              <p>
                Формулировка, которую высоко оценил DeepSeek, не обязана получить такой же
                балл у OpenAI, Claude или Gemini.
              </p>
            </article>
            <article>
              <span>03</span>
              <h3>Нужен широкий, а не удобный результат</h3>
              <p>
                Цель — убрать слабые места для нескольких AI-ревьюеров и повысить устойчивость
                резюме к неизвестному способу первичного отбора.
              </p>
            </article>
          </div>
          <article className="research-card">
            <div className="research-stat">
              <strong>+23–60%</strong>
              <span>
                выше вероятность попасть в шорт-лист относительно равно квалифицированных
                кандидатов с резюме от человека
              </span>
            </div>
            <div className="research-copy">
              <h3>Нейросети могут предпочитать тексты «своего» авторства</h3>
              <p>
                В симуляциях найма вероятность попасть в шорт-лист была на 23–60% выше
                у кандидатов, чьи резюме подготовила та же LLM, которая затем их оценивала.
                Их сравнивали с равно квалифицированными кандидатами, подавшими резюме,
                написанные человеком.
              </p>
              <small>
                Рабочее исследование 2025 года по 24 профессиям. Это предварительный
                результат, а не гарантия решения конкретного работодателя.
              </small>
              <div className="research-links">
                <a
                  href="https://arxiv.org/abs/2509.00462"
                  target="_blank"
                  rel="noreferrer"
                >
                  Открыть исследование <ArrowRight size={15} />
                </a>
                <a
                  href="https://www.rhsmith.umd.edu/news/ai-hiring-tools-may-favor-their-own-work-smith-study-finds"
                  target="_blank"
                  rel="noreferrer"
                >
                  Разбор University of Maryland
                </a>
              </div>
            </div>
          </article>
        </section>

        <section className="content-section" id="how">
          <div className="section-heading">
            <span>Как повысить устойчивость резюме</span>
            <h2>Сделайте отклик понятным большему числу AI-ревьюеров</h2>
            <p>
              Не оптимизируйте текст под одну любимую модель: найдите расхождения,
              исправьте общие слабые места и проверьте результат снова.
            </p>
          </div>
          <div className="steps-grid">
            <Step number="01" icon={<Upload />} title="Загрузите пару">
              PDF, DOCX, публичная ссылка или обычный текст — отдельно для резюме и вакансии.
            </Step>
            <Step number="02" icon={<LockKeyhole />} title="Проверьте обезличивание">
              Контакты заменяются placeholders. Распознанный текст можно поправить до отправки.
            </Step>
            <Step number="03" icon={<Layers3 />} title="Сравните 5 или 10 моделей">
              Каждая LLM вызывается отдельно: вы сразу видите высокий балл одной и провал у другой.
            </Step>
            <Step number="04" icon={<WandSparkles />} title="Примените правки автоматически">
              AI собирает общие рекомендации, дорабатывает резюме без новых фактов и запускает контрольную проверку.
            </Step>
          </div>
        </section>

        <section className="autofix-section">
          <div className="autofix-copy">
            <span>Резюме под ключ</span>
            <h2>Закрываем найденные проблемы вторым запросом</h2>
            <p>
              Не нужно вручную переносить десятки рекомендаций. Сервис сам применяет
              совместимые правки, сохраняет факты и повторно показывает результат у тех же моделей.
            </p>
            <Link className="button ai-action large" to={me ? "/app/new" : "/auth"}>
              Запустить полный цикл <WandSparkles />
            </Link>
          </div>
          <div className="autofix-flow">
            <article><span>01</span><strong>Первичный прогон</strong><p>10 независимых оценок находят общие слабые места.</p></article>
            <ArrowRight />
            <article><span>02</span><strong>Автодоработка</strong><p>Большинство безопасных правок применяется без выдумывания опыта.</p></article>
            <ArrowRight />
            <article><span>03</span><strong>Проверка эффекта</strong><p>Ещё 10 оценок подтверждают, насколько выросла медиана.</p></article>
          </div>
          <p className="autofix-guarantee">
            Резерв — 80 токенов. Если целевой рост не достигнут или стартовая оценка уже 85+, итог пересчитывается до базовой цены 50 токенов.
          </p>
        </section>

        <section className="rubric-section">
          <div className="section-heading left">
            <span>Рубрика v1</span>
            <h2>Оценка, которую можно объяснить</h2>
            <p>
              Итоговый индекс собирается из шести измеримых частей. Сервер проверяет арифметику
              каждой модели и сам считает медиану.
            </p>
          </div>
          <div className="rubric-list">
            {[
              ["Обязательные требования", 30],
              ["Релевантный опыт", 20],
              ["Навыки и ключевые слова", 20],
              ["Доказанные достижения", 15],
              ["Уровень, масштаб и домен", 10],
              ["Ясность и машиночитаемость", 5]
            ].map(([label, value]) => (
              <div className="rubric-row" key={label}>
                <div><span>{label}</span><strong>{value}</strong></div>
                <div className="rubric-track"><i style={{ width: `${Number(value) * 3.1}%` }} /></div>
              </div>
            ))}
          </div>
        </section>

        <section className="content-section" id="pricing">
          <div className="section-heading">
            <span>Оплата без подписки</span>
            <h2>Платите только за нужное действие</h2>
            <p>500 ₽ дают 500 токенов. Токены не сгорают, обязательного тарифа нет.</p>
          </div>
          <div className="pricing-grid">
            <article className="price-card">
              <div className="price-tag">Старт</div>
              <h3>Бесплатно</h3>
              <div className="price">0 ₽</div>
              <p>Пять анализов на аккаунт</p>
              <ul>
                <li><Check /> 5 ведущих моделей</li>
                <li><Check /> Баллы моделей, критериев и общий индекс</li>
                <li><Check /> Детальный разбор рассчитан заранее</li>
                <li><Coins /> Открыть весь разбор — 5 токенов</li>
              </ul>
              <Link className="button secondary full" to={me ? "/app/new" : "/auth"}>Начать бесплатно</Link>
            </article>
            <article className="price-card">
              <div className="price-tag">Глубокий взгляд</div>
              <h3>Расширенный</h3>
              <div className="price">50 <small>токенов</small></div>
              <p>За один десятимодельный прогон</p>
              <ul>
                <li><Check /> 10 независимых моделей</li>
                <li><Check /> Рекомендации открыты сразу</li>
                <li><Check /> Возврат за недоступные модели</li>
                <li><WandSparkles className="ai-marker" /> Переписать резюме — 30 токенов</li>
                <li><FileCheck2 /> Переписать и проверить соответствие — 75 токенов</li>
              </ul>
              <Link className="button secondary full" to={me ? "/app/new" : "/auth"}>Запустить 10 моделей</Link>
            </article>
            <article className="price-card featured">
              <div className="price-tag">Полный цикл</div>
              <h3>Резюме под ключ</h3>
              <div className="price">80 <small>токенов</small></div>
              <p>Анализ, доработка и контроль</p>
              <ul>
                <li><Layers3 /> Два прогона по 10 моделей</li>
                <li><WandSparkles className="ai-marker" /> Большинство правок применяется автоматически</li>
                <li><FileCheck2 /> Улучшенная версия и DOCX</li>
                <li><Check /> 50 токенов при слабом эффекте или старте 85+</li>
              </ul>
              <Link className="button primary full" to={me ? "/app/new" : "/auth"}>Заказать под ключ</Link>
            </article>
          </div>
        </section>

        <section className="faq-section" id="faq">
          <div className="section-heading left">
            <span>FAQ</span><h2>Что важно знать</h2>
          </div>
          <div className="faq-grid">
            <Faq q="Это прогноз прохождения ATS?">
              Нет. Это симуляция LLM-оценки по единой рубрике — индекс соответствия, а не гарантия решения работодателя.
            </Faq>
            <Faq q="Почему недостаточно проверить резюме в одной нейросети?">
              Модели по-разному взвешивают опыт, навыки и формулировки. Сравнение нескольких оценок показывает слепые зоны одной LLM.
            </Faq>
            <Faq q="Нужно ли знать, какой моделью пользуется HR?">
              Нет. В этом и смысл: проверить отклик у нескольких AI-ревьюеров и устранить причины низкой оценки до реального отбора.
            </Faq>
            <Faq q="Мои контакты попадут нейросетям?">
              Перед внешним вызовом ФИО, телефон, email, точный адрес и URL заменяются placeholders.
            </Faq>
            <Faq q="Что будет, если модель недоступна?">
              Вы увидите частичный результат «N из M». В расширенном прогоне неизрасходованный резерв вернётся.
            </Faq>
            <Faq q="Сервис может придумать мне опыт?">
              Нет. При переписывании запрещено добавлять опыт, навыки и достижения; неизвестное отмечается для уточнения.
            </Faq>
            <Faq q="Что входит в «Резюме под ключ»?">
              Первичный анализ 10 моделями, автоматическое применение безопасных правок и повторный прогон. Целевой рост — 15 пунктов при старте ниже 50, 10 при старте ниже 75 и 5 при старте ниже 85. Если цель не достигнута или стартовая оценка уже 85+, итоговая стоимость снижается до 50 токенов.
            </Faq>
          </div>
        </section>

        <section className="cta-section">
          <div>
            <span>Не знаете, какой AI будет у работодателя?</span>
            <h2>Получите анализ и улучшенное резюме за один запуск</h2>
            <p>
              Найдите расхождения, автоматически примените общие правки и сразу проверьте,
              выросла ли оценка до реального отклика.
            </p>
          </div>
          <Link className="button primary large" to={me ? "/app/new" : "/auth"}>
            Проверить у 5 моделей бесплатно <ArrowRight size={19} />
          </Link>
        </section>
      </main>

      <footer>
        <Brand />
        <p>Индекс соответствия по единой рубрике. Не является гарантией трудоустройства.</p>
        <div><Link to="/privacy">Приватность</Link><a href="mailto:support@cvreviewer.fa72.ru">Поддержка</a></div>
      </footer>
    </div>
  );
}

function ScorePreview() {
  const scores = [
    ["DeepSeek", 82, "high"],
    ["OpenAI GPT‑5.6 Luna", 74, "mid"],
    ["Claude Sonnet", 78, "high"],
    ["Qwen", 69, "mid"],
    ["Gemini", 76, "high"]
  ];
  return (
    <div className="score-preview">
      <div className="preview-top">
        <div>
          <span>Медиана 5 AI-ревьюеров</span>
          <strong>76<small>/100</small></strong>
        </div>
        <div className="score-ring"><span>76%</span></div>
      </div>
      <div className="range-line"><span>Один отклик — разные оценки</span><strong>69–82</strong></div>
      <div className="model-scores">
        {scores.map(([name, score, tone]) => (
          <div key={name}>
            <span className={`dot ${tone}`} />
            <p>{name}</p>
            <div className="mini-track"><i style={{ width: `${score}%` }} /></div>
            <strong>{score}</strong>
          </div>
        ))}
      </div>
      <div className="preview-note"><BadgeCheck size={17} /> Получено 5 из 5 независимых оценок</div>
    </div>
  );
}

function Step({ number, icon, title, children }: { number: string; icon: ReactNode; title: string; children: ReactNode }) {
  return <article className="step-card"><div><span>{number}</span>{icon}</div><h3>{title}</h3><p>{children}</p></article>;
}

function Faq({ q, children }: { q: string; children: ReactNode }) {
  return <article><h3>{q}</h3><p>{children}</p></article>;
}

function Privacy() {
  return (
    <div className="legal-page">
      <header className="site-header"><Brand /><Link className="button ghost" to="/">На главную</Link></header>
      <main>
        <div className="eyebrow">Приватность</div>
        <h1>Вы контролируете текст до отправки моделям</h1>
        <p>
          CVReviewer извлекает текст из документа без LLM, заменяет контактные данные placeholders
          и просит вас подтвердить обезличенную версию. Только после явного согласия эта версия
          передаётся внешним провайдерам через Polza AI.
        </p>
        <h2>Что хранится</h2>
        <p>
          Подтверждённый текст, таблица замен и результаты шифруются. Исходный файл удаляется после
          успешного извлечения; при ошибке — не позднее 24 часов. Подтверждённые документы остаются
          в вашей библиотеке для будущих сравнений, пока вы не удалите их отдельно. Удаление проверки
          удаляет её снимки, результаты и версии переписанного резюме.
        </p>
        <h2>Аккаунт и hh.ru</h2>
        <p>
          CVReviewer не получает пароль hh.ru: подключение выполняется через OAuth, а access/refresh
          токены хранятся только в зашифрованном виде. Смена email требует текущий пароль и ссылку
          на новом адресе. Удаление аккаунта выполняется только после отдельного подтверждения,
          отправленного на текущий email.
        </p>
      </main>
    </div>
  );
}

function AuthPage({ me, onAuthenticated }: { me: Me | null; onAuthenticated: () => Promise<void> }) {
  const navigate = useNavigate();
  const [step, setStep] = useState<"email" | "password">("email");
  const [authMode, setAuthMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [hhAgreementAccepted, setHhAgreementAccepted] = useState(false);
  const [confirmationUrl, setConfirmationUrl] = useState<string | null>(null);
  const [confirmationSent, setConfirmationSent] = useState(false);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (me) navigate("/app", { replace: true });
  }, [me, navigate]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNeedsConfirmation(false);
    try {
      if (step === "email") {
        const normalizedEmail = email.trim().toLowerCase();
        if (!isValidEmail(normalizedEmail)) {
          setError("Введите корректный email, например name@example.com.");
          return;
        }
        const status = await api<{ exists: boolean }>("/api/v1/auth/email-status", {
          method: "POST",
          body: JSON.stringify({ email: normalizedEmail })
        });
        setEmail(normalizedEmail);
        setAuthMode(status.exists ? "login" : "register");
        setPassword("");
        setPasswordConfirmation("");
        setHhAgreementAccepted(false);
        setStep("password");
      } else if (authMode === "register") {
        const validationError = registrationPasswordError(password, passwordConfirmation);
        if (validationError) {
          setError(validationError);
          return;
        }
        if (!hhAgreementAccepted) {
          setError("Примите оферту hh.ru для соискателей.");
          return;
        }
        const result = await api<{ mockConfirmationUrl?: string | null }>("/api/v1/auth/register", {
          method: "POST",
          body: JSON.stringify({ email, password, passwordConfirmation, hhAgreementAccepted })
        });
        setConfirmationUrl(result.mockConfirmationUrl ?? null);
        setConfirmationSent(true);
      } else {
        if (!password) {
          setError("Введите пароль.");
          return;
        }
        await api("/api/v1/auth/login", {
          method: "POST",
          body: JSON.stringify({ email, password })
        });
        await onAuthenticated();
        navigate("/app");
      }
    } catch (e) {
      setError(messageOf(e));
      if (e instanceof ApiError && e.code === "email_not_confirmed") {
        setNeedsConfirmation(true);
      }
    } finally {
      setBusy(false);
    }
  }

  async function resendConfirmation() {
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ mockConfirmationUrl?: string | null }>(
        "/api/v1/auth/resend-confirmation",
        {
          method: "POST",
          body: JSON.stringify({ email, password })
        }
      );
      setConfirmationUrl(result.mockConfirmationUrl ?? null);
      setConfirmationSent(true);
      setNeedsConfirmation(false);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  }

  async function confirmMock() {
    if (!confirmationUrl) return;
    setBusy(true);
    try {
      const url = new URL(confirmationUrl);
      await api(`${url.pathname}${url.search}`);
      setConfirmationUrl(null);
      await api("/api/v1/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password })
      });
      await onAuthenticated();
      navigate("/app");
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <Link className="auth-brand" to="/"><Brand /></Link>
      <div className="auth-visual">
        <div>
          <div className="eyebrow light"><Layers3 size={15} /> Сравните пять взглядов</div>
          <h1>Не гадайте, почему резюме отсекают</h1>
          <p>Посмотрите на расхождения моделей и исправьте то, с чем они согласны.</p>
        </div>
        <ScorePreview />
      </div>
      <div className="auth-panel">
        <div className="auth-form-wrap">
          {confirmationSent ? (
            <div className="confirmation-card">
              <div className="success-icon"><BadgeCheck /></div>
              <h2>Подтвердите email</h2>
              <p>
                {confirmationUrl
                  ? "Почта работает в тестовом режиме; одноразовая ссылка доступна ниже."
                  : `Мы отправили одноразовую ссылку на ${email}.`}
              </p>
              {confirmationUrl && <button className="button primary full" onClick={confirmMock} disabled={busy}>
                Подтвердить тестовую почту
              </button>}
            </div>
          ) : (
            <>
              <span className="form-kicker">{step === "email" ? "Вход и регистрация" : authMode === "login" ? "С возвращением" : "Новый аккаунт"}</span>
              <h2>{step === "email" ? "Введите email" : authMode === "login" ? "Введите пароль" : "Придумайте пароль"}</h2>
              <p>{step === "email" ? "Продолжите со входом или создайте новый аккаунт." : authMode === "login" ? `Вход для ${email}` : `Регистрация для ${email}. Без карты и подписки.`}</p>
              <form onSubmit={submit} noValidate>
                {step === "email" ? (
                  <label>Email<input autoFocus autoComplete="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" /></label>
                ) : (
                  <>
                    <label>
                      Пароль
                      <input
                        autoFocus
                        autoComplete={authMode === "login" ? "current-password" : "new-password"}
                        aria-describedby={authMode === "register" ? "password-requirements" : undefined}
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={authMode === "login" ? "Введите пароль" : "Минимум 10 символов"}
                      />
                    </label>
                    {authMode === "register" && (
                      <>
                        <p className="password-requirements" id="password-requirements">
                          Не менее 10 символов, одна заглавная буква, одна строчная буква и одна цифра.
                        </p>
                        <label>
                          Повторите пароль
                          <input
                            autoComplete="new-password"
                            type="password"
                            required
                            value={passwordConfirmation}
                            onChange={(e) => setPasswordConfirmation(e.target.value)}
                            placeholder="Введите пароль ещё раз"
                          />
                        </label>
                        <label className="auth-agreement">
                          <input
                            type="checkbox"
                            required
                            checked={hhAgreementAccepted}
                            onChange={(event) => setHhAgreementAccepted(event.target.checked)}
                          />
                          <span>
                            Регистрируясь, я подтверждаю, что ознакомлен и принимаю условия{` `}
                            <a href="https://hh.ru/account/agreement" target="_blank" rel="noreferrer">
                              оферты hh.ru для соискателей
                            </a>.
                          </span>
                        </label>
                      </>
                    )}
                  </>
                )}
                {error && <div className="form-error"><CircleAlert size={17} />{error}</div>}
                <button className="button primary full large" disabled={busy}>
                  {busy ? <><RefreshCw className="spin" size={18} /> Подождите</> : step === "email" ? "Продолжить" : authMode === "login" ? "Войти" : "Создать аккаунт"}
                </button>
              </form>
              {needsConfirmation && step === "password" && (
                <button
                  className="switch-auth"
                  disabled={busy}
                  onClick={resendConfirmation}
                >
                  Отправить письмо подтверждения ещё раз
                </button>
              )}
              {step === "password" && <button className="switch-auth" onClick={() => { setStep("email"); setPassword(""); setPasswordConfirmation(""); setError(null); }}>Изменить email</button>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AppShell({
  me,
  onRefresh,
  children
}: {
  me: Me;
  onRefresh: () => Promise<void>;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const [navOpen, setNavOpen] = useState(false);
  async function logout() {
    await api("/api/v1/auth/logout", { method: "POST" });
    await onRefresh();
    navigate("/");
  }
  return (
    <div className="app-layout">
      <aside className={navOpen ? "app-sidebar open" : "app-sidebar"}>
        <div className="sidebar-head"><Brand /><button className="mobile-only icon-button" onClick={() => setNavOpen(false)}><X /></button></div>
        <nav>
          <NavLink to="/app" end={true}><Gauge /> Обзор</NavLink>
          <NavLink to="/app/new"><Plus /> Новый анализ</NavLink>
          <NavLink to="/app/profile"><UserRound /> Профиль</NavLink>
        </nav>
        <div className="sidebar-bottom">
          <div className="wallet-mini"><Coins /><div><span>Токены</span><strong>{me.tokenBalance}</strong></div></div>
          <button onClick={logout}><LogOut /> Выйти</button>
        </div>
      </aside>
      {navOpen && (
        <button
          className="sidebar-backdrop"
          aria-label="Закрыть боковое меню"
          onClick={() => setNavOpen(false)}
        />
      )}
      <div className="app-main">
        <header className="app-topbar">
          <button
            className="mobile-only icon-button"
            aria-label="Открыть боковое меню"
            aria-expanded={navOpen}
            onClick={() => setNavOpen(true)}
          >
            <Menu />
          </button>
          <Link className="profile-link" to="/app/profile"><span>{me.email}</span><i>{me.email.slice(0, 1).toUpperCase()}</i></Link>
        </header>
        <main>{children}</main>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, note }: { icon: ReactNode; label: string; value: string; note: string }) {
  return <article className="stat-card"><div>{icon}</div><span>{label}</span><strong>{value}</strong><small>{note}</small></article>;
}

function ReviewTable({ reviews }: { reviews: ReviewSummary[] }) {
  return (
    <div className="review-table">
      {reviews.map((review) => (
        <Link to={`/app/reviews/${review.id}`} key={review.id} className="review-row">
          <div className="review-score">{review.aggregateScore == null ? "—" : Math.round(review.aggregateScore)}</div>
          <div className="review-row-copy">
            <div><span>Резюме</span><strong>{review.resumeTitle}</strong></div>
            <div><span>Вакансия</span><strong>{review.vacancyTitle}</strong></div>
            <small>{reviewModeLabel(review.mode)} · {formatDate(review.createdAt)}</small>
          </div>
          <Status status={review.status} />
          <span>{review.successfulModelCount} из {review.requestedModelCount} моделей</span>
          <ChevronRight />
        </Link>
      ))}
    </div>
  );
}

function EmptyState() {
  return <div className="empty-state"><FileText /><h3>Проверок пока нет</h3><p>Загрузите резюме и вакансию, чтобы получить первый индекс.</p><Link className="button secondary" to="/app/new">Начать анализ</Link></div>;
}

function OverviewPage({ me }: { me: Me }) {
  const [documents, setDocuments] = useState<SavedDocument[]>([]);
  const [reviews, setReviews] = useState<ReviewSummary[]>([]);
  const [optimizations, setOptimizations] = useState<OptimizationSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api<{ items: SavedDocument[] }>("/api/v1/documents?pageSize=100"),
      api<{ items: ReviewSummary[] }>("/api/v1/reviews?pageSize=50"),
      api<{ items: OptimizationSummary[] }>("/api/v1/rewrites?pageSize=100")
    ])
      .then(([documentResult, reviewResult, optimizationResult]) => {
        setDocuments(documentResult.items);
        setReviews(reviewResult.items);
        setOptimizations(optimizationResult.items);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="app-page">
      <div className="page-title">
        <div><span>Личный кабинет</span><h1>Обзор</h1><p>Документы, проверки и версии резюме — в одном месте.</p></div>
        <Link className="button primary" to="/app/new"><Plus size={18} /> Новый анализ</Link>
      </div>
      <div className="stat-grid">
        <Stat icon={<FileCheck2 />} label="Бесплатных проверок" value={String(me.freeReviewsRemaining)} note="из 5 навсегда" />
        <Stat icon={<Coins />} label="Баланс" value={String(me.tokenBalance)} note="1 ₽ = 1 токен" />
        <Stat icon={<Layers3 />} label="Моделей в прогоне" value="5–10" note="независимых оценок" />
      </div>
      {loading ? <section className="panel"><InlineLoader /></section> : (
        <div className="overview-sections">
        <section className="panel" aria-labelledby="documents-heading">
          <div className="panel-head"><div><h2 id="documents-heading">Резюме и вакансии</h2><p>Используйте загруженные документы повторно без новой загрузки.</p></div></div>
          {documents.length ? (
            <div className="document-library">
              {documents.map((document) => {
                const resume = document.kind === "Resume";
                const parameter = resume ? "resumeDocumentId" : "vacancyDocumentId";
                return (
                  <article key={document.id}>
                    <div className="document-library-icon">{resume ? <FileText /> : <BriefcaseBusiness />}</div>
                    <div><span>{resume ? "Резюме" : "Вакансия"} · {formatDate(document.updatedAt)}</span><h3>{document.title}</h3><p>{document.preview}</p></div>
                    <div className="document-library-actions">
                      <Link className="button secondary" to={`/app/new?${parameter}=${document.id}&intent=compare`}>{resume ? "Сравнить с вакансией" : "Сравнить с резюме"}</Link>
                      <Link className="button ai-action" to={`/app/new?${parameter}=${document.id}&intent=optimize`}><WandSparkles /> {resume ? "Оптимизировать под вакансию" : "Оптимизировать резюме"}</Link>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : <div className="empty-state"><FolderOpen /><h3>Документов пока нет</h3><p>Подтверждённые резюме и вакансии появятся после загрузки.</p></div>}
        </section>
        <section className="panel" aria-labelledby="reviews-heading">
          <div className="panel-head"><div><h2 id="reviews-heading">История проверок</h2><p>Все выполненные и запущенные анализы.</p></div></div>
          {reviews.length ? <ReviewTable reviews={reviews} /> : <EmptyState />}
        </section>
        <section className="panel" aria-labelledby="optimizations-heading">
          <div className="panel-head"><div><h2 id="optimizations-heading">История оптимизаций</h2><p>Каждая версия резюме вместе с вакансией, для которой она создана.</p></div></div>
          {optimizations.length ? (
            <div className="optimization-list">
              {optimizations.map((item) => (
                <Link key={item.id} to={`/app/reviews/${item.reviewId}?rewrite=${item.id}`}>
                  <div><WandSparkles /><strong>Версия {item.version}</strong><Status status={item.status} /></div>
                  <p><span>Резюме</span>{item.resumeTitle}</p>
                  <p><span>Вакансия</span>{item.vacancyTitle}</p>
                  <small>{formatDate(item.createdAt)}</small>
                  <ChevronRight />
                </Link>
              ))}
            </div>
          ) : <div className="empty-state"><WandSparkles /><h3>Оптимизаций пока нет</h3><p>Созданные версии резюме появятся здесь.</p></div>}
        </section>
        </div>
      )}
    </div>
  );
}

type InputSource = {
  sourceType: "Text" | "File" | "Url" | "Saved" | "Hh";
  text: string;
  url: string;
  file: File | null;
  documentId: string;
};

const emptyInputSource = (): InputSource => ({
  sourceType: "Text",
  text: "",
  url: "",
  file: null,
  documentId: ""
});

function NewReview({ onChanged }: { onChanged: () => Promise<void> }) {
  const navigate = useNavigate();
  const query = new URLSearchParams(window.location.search);
  const preselectedResume = query.get("resumeDocumentId") ?? "";
  const preselectedVacancy = query.get("vacancyDocumentId") ?? "";
  const [step, setStep] = useState(1);
  const [resume, setResume] = useState<InputSource>(() => preselectedResume
    ? { ...emptyInputSource(), sourceType: "Saved", documentId: preselectedResume }
    : emptyInputSource());
  const [vacancy, setVacancy] = useState<InputSource>(() => preselectedVacancy
    ? { ...emptyInputSource(), sourceType: "Saved", documentId: preselectedVacancy }
    : emptyInputSource());
  const [savedDocuments, setSavedDocuments] = useState<SavedDocument[]>([]);
  const [hhStatus, setHhStatus] = useState<HhStatus | null>(null);
  const [hhResumes, setHhResumes] = useState<HhResume[]>([]);
  const [documents, setDocuments] = useState<{ resume?: DocumentResult; vacancy?: DocumentResult }>({});
  const [mode, setMode] = useState<"FreeFive" | "PremiumTen" | "Turnkey">(
    query.get("intent") === "optimize" ? "Turnkey" : "FreeFive"
  );
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<{ items: SavedDocument[] }>("/api/v1/documents?pageSize=100")
      .then((result) => setSavedDocuments(result.items))
      .catch(() => setSavedDocuments([]));
    api<HhStatus>("/api/v1/integrations/hh/status")
      .then(async (status) => {
        setHhStatus(status);
        if (status.connected && status.available) {
          const result = await api<{ items: HhResume[] }>("/api/v1/integrations/hh/resumes");
          setHhResumes(result.items);
        }
      })
      .catch(() => setHhStatus({ available: false, mode: "Mock", connected: false }));
  }, []);

  function connectHh() {
    navigate("/app/profile");
  }

  async function uploadOne(kind: "Resume" | "Vacancy", source: InputSource): Promise<DocumentResult> {
    if (source.sourceType === "Saved") {
      if (!source.documentId) throw new Error("Выберите сохранённый документ.");
      const document = await api<DocumentResult>(`/api/v1/documents/${source.documentId}`);
      if (document.kind !== kind || document.status !== "Confirmed") {
        throw new Error("Сохранённый документ больше недоступен для анализа.");
      }
      return { ...document, reused: true };
    }
    if (source.sourceType === "Hh") {
      if (kind !== "Resume" || !source.documentId) {
        throw new Error("Выберите резюме из подключённого аккаунта hh.ru.");
      }
      return api<DocumentResult>(
        `/api/v1/integrations/hh/resumes/${encodeURIComponent(source.documentId)}/import`,
        { method: "POST" }
      );
    }

    const form = new FormData();
    form.set("kind", kind);
    form.set("sourceType", source.sourceType);
    if (source.sourceType === "Text") form.set("text", source.text);
    if (source.sourceType === "Url") form.set("url", source.url);
    if (source.sourceType === "File" && source.file) form.set("file", source.file);
    const created = await api<{ id: string }>("/api/v1/documents", { method: "POST", body: form });
    for (let attempt = 0; attempt < 90; attempt++) {
      await delay(1500);
      const current = await api<DocumentResult>(`/api/v1/documents/${created.id}`);
      if (current.status === "AwaitingConfirmation") return current;
      if (current.status === "Failed") throw new Error(current.failureReason ?? "Не удалось извлечь текст.");
    }
    throw new Error("Извлечение заняло слишком много времени.");
  }

  async function extract() {
    setBusy(true); setError(null);
    try {
      const [resumeDocument, vacancyDocument] = await Promise.all([
        uploadOne("Resume", resume),
        uploadOne("Vacancy", vacancy)
      ]);
      setDocuments({ resume: resumeDocument, vacancy: vacancyDocument });
      setStep(2);
    } catch (e) { setError(messageOf(e)); } finally { setBusy(false); }
  }

  async function confirmTexts() {
    if (!documents.resume || !documents.vacancy) return;
    setBusy(true); setError(null);
    try {
      const requests: Array<Promise<unknown>> = [];
      if (!documents.resume.reused) {
        requests.push(api(`/api/v1/documents/${documents.resume.id}/confirm`, {
          method: "PATCH",
          body: JSON.stringify({ text: documents.resume.extractedText })
        }));
      }
      if (!documents.vacancy.reused) {
        requests.push(api(`/api/v1/documents/${documents.vacancy.id}/confirm`, {
          method: "PATCH",
          body: JSON.stringify({ text: documents.vacancy.extractedText })
        }));
      }
      await Promise.all(requests);
      setStep(3);
    } catch (e) { setError(messageOf(e)); } finally { setBusy(false); }
  }

  async function launch() {
    if (!documents.resume || !documents.vacancy) return;
    setBusy(true); setError(null);
    try {
      const result = await api<{ id: string }>("/api/v1/reviews", {
        method: "POST",
        idempotencyKey: newIdempotencyKey("review"),
        body: JSON.stringify({
          resumeDocumentId: documents.resume.id,
          vacancyDocumentId: documents.vacancy.id,
          mode,
          externalProcessingConsent: consent
        })
      });
      await onChanged();
      navigate(`/app/reviews/${result.id}`);
    } catch (e) { setError(messageOf(e)); } finally { setBusy(false); }
  }

  const validSources = sourceReady(resume) && sourceReady(vacancy);
  const extractedTextsValid = [documents.resume, documents.vacancy].every((document) => {
    const length = document?.extractedText?.trim().length ?? 0;
    return length >= 80 && length <= 25_000;
  });
  return (
    <div className="app-page narrow-page">
      <div className="page-title"><div><span>Новая проверка</span><h1>{step === 1 ? "Добавьте или выберите документы" : step === 2 ? "Проверьте текст" : "Выберите глубину"}</h1><p>Шаг {step} из 3</p></div></div>
      <div className="wizard-progress"><i className={step >= 1 ? "done" : ""} /><i className={step >= 2 ? "done" : ""} /><i className={step >= 3 ? "done" : ""} /></div>
      {step === 1 && (
        <>
          <div className="document-grid">
            <DocumentInput
              title="Ваше резюме"
              kind="Резюме"
              value={resume}
              savedDocuments={savedDocuments.filter((item) => item.kind === "Resume")}
              hhStatus={hhStatus}
              hhResumes={hhResumes}
              onConnectHh={() => void connectHh()}
              onChange={setResume}
            />
            <DocumentInput
              title="Текст вакансии"
              kind="Вакансия"
              value={vacancy}
              savedDocuments={savedDocuments.filter((item) => item.kind === "Vacancy")}
              onChange={setVacancy}
            />
          </div>
          <div className="limit-note"><ShieldCheck /><p><strong>До отправки LLM ничего не происходит.</strong> Сначала worker извлечёт текст и обезличит контакты. Файл до 10 MiB, PDF до 50 страниц, текст до 25 000 символов.</p></div>
          {error && <ErrorBox>{error}</ErrorBox>}
          <div className="wizard-actions"><button className="button primary large" disabled={!validSources || busy} onClick={extract}>{busy ? <><RefreshCw className="spin" /> Готовим текст</> : <>Продолжить с документами <ArrowRight /></>}</button></div>
        </>
      )}
      {step === 2 && documents.resume && documents.vacancy && (
        <>
          <div className="editor-stack">
            <TextEditor label="Резюме" readOnly={documents.resume.reused} value={documents.resume.extractedText ?? ""} replacements={documents.resume.replacements} onChange={(text) => setDocuments((value) => ({ ...value, resume: { ...value.resume!, extractedText: text } }))} />
            <TextEditor label="Вакансия" readOnly={documents.vacancy.reused} value={documents.vacancy.extractedText ?? ""} replacements={documents.vacancy.replacements} onChange={(text) => setDocuments((value) => ({ ...value, vacancy: { ...value.vacancy!, extractedText: text } }))} />
          </div>
          {error && <ErrorBox>{error}</ErrorBox>}
          <div className="wizard-actions split"><button className="button ghost" onClick={() => setStep(1)}>Назад</button><button className="button primary large" disabled={busy || !extractedTextsValid} onClick={confirmTexts}>Подтвердить тексты <ArrowRight /></button></div>
        </>
      )}
      {step === 3 && (
        <>
          <div className="mode-grid">
            <button className={mode === "FreeFive" ? "mode-card selected" : "mode-card"} onClick={() => setMode("FreeFive")}>
              <div><span>Бесплатный</span><i>{mode === "FreeFive" && <Check />}</i></div><h2>5 моделей</h2><strong>1 проверка</strong><p>Баллы моделей, критериев и общий индекс сразу. Текстовый разбор можно открыть за 5 токенов.</p>
            </button>
            <button className={mode === "PremiumTen" ? "mode-card selected" : "mode-card"} onClick={() => setMode("PremiumTen")}>
              <div><span>Расширенный</span><i>{mode === "PremiumTen" && <Check />}</i></div><h2>10 моделей</h2><strong>50 токенов</strong><p>Полный разбор всех успешных моделей включён и виден сразу.</p>
            </button>
            <button className={mode === "Turnkey" ? "mode-card selected turnkey-mode" : "mode-card turnkey-mode"} onClick={() => setMode("Turnkey")}>
              <div><span>Под ключ</span><i>{mode === "Turnkey" && <Check />}</i></div><h2>2 × 10 моделей</h2><strong>до 80 токенов</strong><p>Анализ, автоматическая доработка и контрольный прогон. При слабом эффекте или старте 85+ — 50 токенов.</p>
            </button>
          </div>
          <label className="consent-check"><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} /><span><strong>Я согласен на внешний анализ обезличенного текста</strong><small>Текст будет передан выбранным LLM через Polza AI. Контакты исключены.</small></span></label>
          {error && <ErrorBox>{error}</ErrorBox>}
          <div className="wizard-actions split"><button className="button ghost" onClick={() => setStep(2)}>Назад</button><button className="button primary large" disabled={!consent || busy} onClick={launch}>{busy ? <><RefreshCw className="spin" /> Запускаем</> : <>Запустить анализ <ArrowRight /></>}</button></div>
        </>
      )}
    </div>
  );
}

function DocumentInput({
  title,
  kind,
  value,
  savedDocuments,
  hhStatus,
  hhResumes = [],
  onConnectHh,
  onChange
}: {
  title: string;
  kind: string;
  value: InputSource;
  savedDocuments: SavedDocument[];
  hhStatus?: HhStatus | null;
  hhResumes?: HhResume[];
  onConnectHh?: () => void;
  onChange: (value: InputSource) => void;
}) {
  const selected = savedDocuments.find((item) => item.id === value.documentId);
  const sources: InputSource["sourceType"][] = kind === "Резюме"
    ? ["Text", "File", "Url", "Saved", "Hh"]
    : ["Text", "File", "Url", "Saved"];
  return (
    <article className="document-card">
      <div className="document-title"><FileText /><div><span>{kind}</span><h2>{title}</h2></div></div>
      <div className="source-tabs">
        {sources.map((source) => (
          <button
            key={source}
            className={value.sourceType === source ? "active" : ""}
            onClick={() => onChange({ ...value, sourceType: source, documentId: "" })}
          >
            {source === "Text" ? "Текст" : source === "File" ? "Файл" : source === "Url" ? "Ссылка" : source === "Hh" ? "hh.ru" : "Сохранённые"}
          </button>
        ))}
      </div>
      {value.sourceType === "Text" && <textarea value={value.text} maxLength={25000} onChange={(e) => onChange({ ...value, text: e.target.value })} placeholder={kind === "Резюме" ? "Вставьте текст резюме…" : "Вставьте описание вакансии…"} />}
      {value.sourceType === "Url" && <div className="url-input"><input type="url" value={value.url} onChange={(e) => onChange({ ...value, url: e.target.value })} placeholder="https://…" /><small>Публичная страница, включая HH.ru best-effort</small></div>}
      {value.sourceType === "File" && <label className="dropzone"><Upload /><strong>{value.file?.name ?? "Выберите PDF или DOCX"}</strong><span>до 10 MiB</span><input type="file" accept=".pdf,.docx" onChange={(e) => onChange({ ...value, file: e.target.files?.[0] ?? null })} /></label>}
      {value.sourceType === "Saved" && (
        <div className="saved-document-picker">
          {savedDocuments.length ? (
            <>
              <label>
                Выберите из библиотеки
                <select value={value.documentId} onChange={(event) => onChange({ ...value, documentId: event.target.value })}>
                  <option value="">Не выбрано</option>
                  {savedDocuments.map((document) => (
                    <option key={document.id} value={document.id}>{document.title}</option>
                  ))}
                </select>
              </label>
              {selected && <div><FolderOpen /><p><strong>{selected.title}</strong><span>{selected.preview}</span></p></div>}
            </>
          ) : (
            <div className="saved-empty"><FolderOpen /><p><strong>Библиотека пока пуста</strong><span>Подтверждённые документы появятся здесь после первой загрузки.</span></p></div>
          )}
        </div>
      )}
      {value.sourceType === "Hh" && (
        <div className="saved-document-picker hh-picker">
          {!hhStatus || !hhStatus.available ? (
            <div className="saved-empty"><BriefcaseBusiness /><p><strong>hh.ru пока в mock-режиме</strong><span>После регистрации приложения здесь появится безопасный вход через OAuth — пароль hh.ru CVReviewer не получает.</span></p></div>
          ) : !hhStatus.connected ? (
            <div className="saved-empty"><BriefcaseBusiness /><p><strong>Подключите hh.ru</strong><span>Вы войдёте на стороне hh.ru и разрешите чтение своих резюме.</span></p><button className="button secondary" onClick={onConnectHh}>Подключить hh.ru</button></div>
          ) : hhResumes.length ? (
            <label>
              Выберите своё резюме hh.ru
              <select value={value.documentId} onChange={(event) => onChange({ ...value, documentId: event.target.value })}>
                <option value="">Не выбрано</option>
                {hhResumes.map((resume) => <option key={resume.id} value={resume.id}>{resume.title}</option>)}
              </select>
            </label>
          ) : (
            <div className="saved-empty"><BriefcaseBusiness /><p><strong>Резюме не найдены</strong><span>В подключённом аккаунте hh.ru нет доступных резюме.</span></p></div>
          )}
        </div>
      )}
      <div className="char-count">{value.sourceType === "Text" ? `${value.text.length.toLocaleString("ru-RU")} / 25 000` : value.sourceType === "Saved" ? "Повторная загрузка не нужна" : "Текст не обрезается автоматически"}</div>
    </article>
  );
}

function TextEditor({ label, value, replacements, readOnly = false, onChange }: { label: string; value: string; replacements?: Record<string, string>; readOnly?: boolean; onChange: (value: string) => void }) {
  return <article className="text-editor"><div><div><span>{readOnly ? "Из библиотеки" : "Проверьте результат"}</span><h2>{label}</h2></div><strong>{value.length.toLocaleString("ru-RU")} знаков</strong></div>{value.length > 25_000 && <ErrorBox>Текст не обрезан. Сократите его до 25 000 знаков, чтобы продолжить.</ErrorBox>}<textarea value={value} readOnly={readOnly} maxLength={Math.max(25_000, value.length)} onChange={(e) => onChange(e.target.value)} />{readOnly && <p className="redaction-info"><FolderOpen /> Уже подтверждённый текст используется без изменений.</p>}{!readOnly && replacements && Object.keys(replacements).length > 0 && <p className="redaction-info"><LockKeyhole /> Найдено и заменено персональных фрагментов: {Object.keys(replacements).length}</p>}</article>;
}

function ProfilePage({ me }: { me: Me }) {
  const [newEmail, setNewEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState("");
  const [deletePassword, setDeletePassword] = useState("");
  const [hhAgreementAccepted, setHhAgreementAccepted] = useState(false);
  const [hhStatus, setHhStatus] = useState<HhStatus | null>(null);
  const [payments, setPayments] = useState<{ available: boolean; mode: string; amounts: number[] } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mockUrl, setMockUrl] = useState<string | null>(null);

  const loadIntegrations = useCallback(async () => {
    const [hh, payment] = await Promise.all([
      api<HhStatus>("/api/v1/integrations/hh/status"),
      api<{ available: boolean; mode: string; amounts: number[] }>("/api/v1/payments/configuration")
    ]);
    setHhStatus(hh);
    setPayments(payment);
  }, []);

  useEffect(() => {
    void loadIntegrations().catch((e) => setError(messageOf(e)));
  }, [loadIntegrations]);

  async function requestEmailChange(event: FormEvent) {
    event.preventDefault(); setBusy("email"); setError(null); setNotice(null); setMockUrl(null);
    try {
      const result = await api<{ destination: string; mockConfirmationUrl?: string | null }>("/api/v1/profile/email/change-request", {
        method: "POST",
        body: JSON.stringify({ newEmail, currentPassword: emailPassword })
      });
      setNotice(`Ссылка подтверждения отправлена на ${result.destination}.`);
      setMockUrl(result.mockConfirmationUrl ?? null);
      setEmailPassword("");
    } catch (e) { setError(messageOf(e)); } finally { setBusy(null); }
  }

  async function changePassword(event: FormEvent) {
    event.preventDefault(); setBusy("password"); setError(null); setNotice(null);
    const validationError = registrationPasswordError(newPassword, newPasswordConfirmation);
    if (validationError) { setError(validationError); setBusy(null); return; }
    try {
      await api("/api/v1/profile/password", {
        method: "POST",
        body: JSON.stringify({ currentPassword, newPassword, newPasswordConfirmation })
      });
      setNotice("Пароль изменён.");
      setCurrentPassword(""); setNewPassword(""); setNewPasswordConfirmation("");
    } catch (e) { setError(messageOf(e)); } finally { setBusy(null); }
  }

  async function requestDeletion(event: FormEvent) {
    event.preventDefault(); setBusy("delete"); setError(null); setNotice(null); setMockUrl(null);
    try {
      const result = await api<{ destination: string; mockConfirmationUrl?: string | null }>("/api/v1/profile/deletion-request", {
        method: "POST",
        body: JSON.stringify({ currentPassword: deletePassword })
      });
      setNotice(`Отдельная ссылка удаления отправлена на ${result.destination}.`);
      setMockUrl(result.mockConfirmationUrl ?? null);
      setDeletePassword("");
    } catch (e) { setError(messageOf(e)); } finally { setBusy(null); }
  }

  async function connectHh() {
    setBusy("hh"); setError(null);
    try {
      const result = await api<{ authorizationUrl: string }>("/api/v1/integrations/hh/connect", {
        method: "POST",
        body: JSON.stringify({ agreementAccepted: hhAgreementAccepted })
      });
      window.location.assign(result.authorizationUrl);
    } catch (e) { setError(messageOf(e)); setBusy(null); }
  }

  async function disconnectHh() {
    setBusy("hh"); setError(null);
    try {
      await api("/api/v1/integrations/hh", { method: "DELETE" });
      await loadIntegrations();
      setNotice("Связь с hh.ru удалена вместе с сохранёнными OAuth-токенами.");
    } catch (e) { setError(messageOf(e)); } finally { setBusy(null); }
  }

  async function topUp(amountRubles: number) {
    setBusy("payment"); setError(null);
    try {
      const order = await api<{ paymentUrl: string }>("/api/v1/payments/orders", {
        method: "POST",
        idempotencyKey: newIdempotencyKey("robokassa"),
        body: JSON.stringify({ amountRubles })
      });
      window.location.assign(order.paymentUrl);
    } catch (e) { setError(messageOf(e)); setBusy(null); }
  }

  return (
    <div className="app-page profile-page">
      <div className="page-title"><div><span>Аккаунт</span><h1>Профиль</h1><p>Управление входом, внешними подключениями и оплатой.</p></div></div>
      {notice && <div className="success-box"><Check /> {notice}{mockUrl && <Link className="button secondary" to={new URL(mockUrl).pathname + new URL(mockUrl).search}>Открыть тестовую ссылку</Link>}</div>}
      {error && <ErrorBox>{error}</ErrorBox>}
      <div className="profile-grid">
        <section className="panel profile-card">
          <div className="profile-card-head"><Mail /><div><h2>Email</h2><p>Текущий: {me.email}</p></div></div>
          <form onSubmit={requestEmailChange}>
            <label>Новый email<input type="email" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)} /></label>
            <label>Текущий пароль<input type="password" autoComplete="current-password" required value={emailPassword} onChange={(e) => setEmailPassword(e.target.value)} /></label>
            <button className="button primary" disabled={!!busy}>{busy === "email" ? "Отправляем…" : "Подтвердить новый email"}</button>
          </form>
        </section>
        <section className="panel profile-card">
          <div className="profile-card-head"><KeyRound /><div><h2>Пароль</h2><p>После проверки текущего пароля.</p></div></div>
          <form onSubmit={changePassword}>
            <label>Текущий пароль<input type="password" autoComplete="current-password" required value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} /></label>
            <label>Новый пароль<input type="password" autoComplete="new-password" required value={newPassword} onChange={(e) => setNewPassword(e.target.value)} /></label>
            <label>Повторите новый пароль<input type="password" autoComplete="new-password" required value={newPasswordConfirmation} onChange={(e) => setNewPasswordConfirmation(e.target.value)} /></label>
            <button className="button primary" disabled={!!busy}>{busy === "password" ? "Сохраняем…" : "Изменить пароль"}</button>
          </form>
        </section>
        <section className="panel profile-card">
          <div className="profile-card-head"><BriefcaseBusiness /><div><h2>hh.ru</h2><p>Импорт собственных резюме через OAuth.</p></div></div>
          {!hhStatus?.available ? <p className="integration-note">Интеграция подготовлена и выключена до регистрации приложения.</p> : hhStatus.connected ? (
            <><p className="integration-ok"><Check /> Аккаунт подключён</p><button className="button secondary" disabled={!!busy} onClick={() => void disconnectHh()}>Отключить hh.ru</button></>
          ) : <>
            <label className="hh-agreement">
              <input type="checkbox" checked={hhAgreementAccepted} onChange={(event) => setHhAgreementAccepted(event.target.checked)} />
              <span>Я принимаю <a href="https://hh.ru/account/agreement" target="_blank" rel="noreferrer">оферту hh.ru для соискателей</a>.</span>
            </label>
            <button className="button secondary" disabled={!!busy || !hhAgreementAccepted} onClick={() => void connectHh()}>{busy === "hh" ? "Переходим…" : "Подключить hh.ru"}</button>
          </>}
        </section>
        <section className="panel profile-card">
          <div className="profile-card-head"><CreditCard /><div><h2>Пополнение</h2><p>1 ₽ = 1 токен, платёж через Robokassa.</p></div></div>
          {!payments?.available ? <p className="integration-note">Robokassa подготовлена и выключена до регистрации магазина.</p> : (
            <div className="payment-amounts">{payments.amounts.map((amount) => <button className="button secondary" disabled={!!busy} key={amount} onClick={() => void topUp(amount)}>{amount} ₽</button>)}</div>
          )}
        </section>
      </div>
      <section className="panel profile-card danger-card">
        <div className="profile-card-head"><Trash2 /><div><h2>Удалить аккаунт</h2><p>Сначала проверим пароль, затем отправим отдельную одноразовую ссылку на текущий email.</p></div></div>
        <form onSubmit={requestDeletion}>
          <label>Текущий пароль<input type="password" autoComplete="current-password" required value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} /></label>
          <button className="button danger" disabled={!!busy}>{busy === "delete" ? "Отправляем…" : "Запросить удаление"}</button>
        </form>
      </section>
    </div>
  );
}

function ProfileConfirmationPage({ kind, onChanged }: { kind: "email" | "delete"; onChanged: () => Promise<void> }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const query = new URLSearchParams(window.location.search);
  const userId = query.get("userId") ?? "";
  const token = query.get("token") ?? "";
  const email = query.get("email") ?? "";

  async function confirm() {
    setBusy(true); setError(null);
    try {
      if (kind === "email") {
        await api("/api/v1/profile/email/confirm", {
          method: "POST",
          body: JSON.stringify({ userId, email, token })
        });
        await onChanged();
        navigate("/app/profile", { replace: true });
      } else {
        await api("/api/v1/profile/deletion-confirm", {
          method: "POST",
          body: JSON.stringify({ userId, token })
        });
        await onChanged();
        navigate("/", { replace: true });
      }
    } catch (e) { setError(messageOf(e)); setBusy(false); }
  }

  return (
    <div className="app-page narrow-page">
      <section className={kind === "delete" ? "panel confirmation-panel danger-card" : "panel confirmation-panel"}>
        {kind === "delete" ? <Trash2 /> : <Mail />}
        <h1>{kind === "delete" ? "Подтвердите удаление аккаунта" : "Подтвердите новый email"}</h1>
        <p>{kind === "delete" ? "После нажатия документы, проверки и версии будут удалены без возможности восстановления." : `Новый адрес: ${email}`}</p>
        {error && <ErrorBox>{error}</ErrorBox>}
        <button className={kind === "delete" ? "button danger large" : "button primary large"} disabled={busy || !userId || !token} onClick={() => void confirm()}>{busy ? "Подтверждаем…" : kind === "delete" ? "Удалить аккаунт окончательно" : "Подтвердить email"}</button>
      </section>
    </div>
  );
}

function ReviewPage({ onChanged }: { onChanged: () => Promise<void> }) {
  const id = usePath().split("/").at(-1);
  const navigate = useNavigate();
  const requestedRewrite = new URLSearchParams(window.location.search).get("rewrite");
  const [review, setReview] = useState<Review | null>(null);
  const [models, setModels] = useState<ExtraModel[]>([]);
  const [selectedModel, setSelectedModel] = useState("");
  const [rewrite, setRewrite] = useState<RewriteDetails | null>(null);
  const [rewriteDraft, setRewriteDraft] = useState("");
  const [rewriteSavedContent, setRewriteSavedContent] = useState("");
  const [rewriteNotice, setRewriteNotice] = useState<string | null>(null);
  const [rewriteOpen, setRewriteOpen] = useState(false);
  const [rewriteOriginal, setRewriteOriginal] = useState("");
  const [rewriteAnswers, setRewriteAnswers] = useState<string[]>([]);

  const [source, setSource] = useState<ReviewSource | null>(null);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    const result = await api<Review>(`/api/v1/reviews/${id}`);
    setReview(result);
  }, [id]);

  const pollRewrite = useCallback(async (rewriteId: string) => {
    for (let attempt = 0; attempt < 90; attempt++) {
      const current = await api<RewriteDetails>(`/api/v1/rewrites/${rewriteId}`);
      setRewrite(current);
      if (current.content) {
        setRewriteDraft(current.content);
        setRewriteSavedContent(current.content);
      }
      if (current.questions) {
        setRewriteAnswers(current.questions.map(() => ""));
      }
      if (current.status === "AwaitingInput") {
        await Promise.all([load(), onChanged()]);
        return;
      }
      if (["Completed", "Failed"].includes(current.status)) {
        await Promise.all([load(), onChanged()]);
        if (current.verificationReviewId) {
          navigate(`/app/reviews/${current.verificationReviewId}`);
          return;
        }
        if (current.status === "Completed" && current.verifyAfterRewrite && !current.failureReason) {
          await delay(1000);
          continue;
        }
        return;
      }
      await delay(2000);
    }
    setError("Создание улучшенной версии или её проверка выполняется дольше ожидаемого. Результат можно открыть позже на этой странице.");
  }, [load, navigate, onChanged]);

  const openRewrite = useCallback(async (rewriteId: string, show = true) => {
    if (show) setRewriteOpen(true);
    setError(null);
    setRewriteNotice(null);
    try {
      const current = await api<RewriteDetails>(`/api/v1/rewrites/${rewriteId}`);
      setRewriteAnswers(current.questions?.map(() => "") ?? []);
      if (!rewriteOriginal && id) {
        void api<ReviewSource>(`/api/v1/reviews/${id}/source/Resume`)
          .then((original) => setRewriteOriginal(original.text))
          .catch(() => undefined);
      }
      setRewrite(current);
      setRewriteDraft(current.content ?? "");
      setRewriteSavedContent(current.content ?? "");
      if (["Queued", "Running"].includes(current.status) ||
          (current.status === "Completed" && current.verifyAfterRewrite && !current.verificationReviewId && !current.failureReason)) {
        void pollRewrite(rewriteId);
      }
    } catch (e) {
      setError(messageOf(e));
    }
  }, [id, pollRewrite, rewriteOriginal]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    api<ExtraModel[]>("/api/v1/models/available").then((value) => { setModels(value); setSelectedModel(value[0]?.modelId ?? ""); });
  }, []);
  useEffect(() => {
    if (!review || !["Queued", "Running"].includes(review.status)) return;
    const timer = window.setInterval(() => void load(), 3000);
    return () => window.clearInterval(timer);
  }, [review, load]);
  useEffect(() => {
    if (requestedRewrite && !rewrite) {
      void openRewrite(requestedRewrite);
      return;
    }
    const latest = review?.rewrites?.[0];
    if (!latest || rewrite) return;
    const active = ["Queued", "Running", "AwaitingInput"].includes(latest.status);
    void openRewrite(latest.id, active);
  }, [review, rewrite, openRewrite, requestedRewrite]);

  async function unlock() {
    setBusy("unlock"); setError(null);
    try {
      await api(`/api/v1/reviews/${id}/recommendations/unlock`, { method: "POST", idempotencyKey: newIdempotencyKey("recommendations") });
      await Promise.all([load(), onChanged()]);
    } catch (e) { setError(messageOf(e)); } finally { setBusy(null); }
  }
  async function addModel() {
    setBusy("model"); setError(null);
    try {
      await api(`/api/v1/reviews/${id}/extra-models`, { method: "POST", idempotencyKey: newIdempotencyKey("extra-model"), body: JSON.stringify({ modelId: selectedModel }) });
      await Promise.all([load(), onChanged()]);
    } catch (e) { setError(messageOf(e)); } finally { setBusy(null); }
  }
  async function createRewrite(verifyAfterRewrite = false) {
    const action = verifyAfterRewrite ? "rewrite-and-review" : "rewrite";
    setBusy(action); setError(null);
    setRewriteNotice(null);
    setRewriteOpen(true);
    try {
      const result = await api<RewriteDetails>(`/api/v1/reviews/${id}/rewrites`, {
        method: "POST",
        idempotencyKey: newIdempotencyKey(action),
        body: JSON.stringify({ verifyAfterRewrite })
      });
      setRewrite({
        ...result,
        version: (review?.rewrites?.[0]?.version ?? 0) + 1,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      setRewriteDraft("");
      setRewriteSavedContent("");
      void pollRewrite(result.id);
      setRewriteAnswers([]);
      if (id) {
        void api<ReviewSource>(`/api/v1/reviews/${id}/source/Resume`)
          .then((original) => setRewriteOriginal(original.text))
          .catch(() => undefined);
      }
      await onChanged();
    } catch (e) { setError(messageOf(e)); } finally { setBusy(null); }
  }
  async function submitRewriteAnswers() {
    if (!rewrite) return;
    setBusy("rewrite-answers"); setError(null); setRewriteNotice(null);
    try {
      const result = await api<{ id: string; status: string }>(`/api/v1/rewrites/${rewrite.id}/answers`, {
        method: "POST",
        idempotencyKey: newIdempotencyKey("rewrite-answers"),
        body: JSON.stringify({ answers: rewriteAnswers })
      });
      setRewrite((current) => current ? { ...current, status: result.status } : current);
      setRewriteNotice("Ответы приняты. Модель продолжает доработку.");
      void pollRewrite(rewrite.id);
      await onChanged();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(null);
    }
  }
  async function saveRewrite() {
    if (!rewrite) return;
    setBusy("rewrite-save"); setError(null); setRewriteNotice(null);
    try {
      const result = await api<{ id: string; updatedAt: string }>(`/api/v1/rewrites/${rewrite.id}`, {
        method: "PATCH",
        body: JSON.stringify({ content: rewriteDraft })
      });
      setRewrite((current) => current ? { ...current, content: rewriteDraft, updatedAt: result.updatedAt } : current);
      setRewriteSavedContent(rewriteDraft);
      setRewriteNotice("Правки сохранены. DOCX обновлён.");
      await load();
    } catch (e) { setError(messageOf(e)); } finally { setBusy(null); }
  }
  async function verifyImprovedResume() {
    if (!rewrite) return;
    setBusy("rewrite-check"); setError(null); setRewriteNotice(null);
    try {
      const result = await api<{ id: string }>(`/api/v1/reviews/${id}/rewrites/${rewrite.id}/verify`, {
        method: "POST",
        idempotencyKey: newIdempotencyKey("rewrite-check")
      });
      await onChanged();
      navigate(`/app/reviews/${result.id}`);
    } catch (e) { setError(messageOf(e)); } finally { setBusy(null); }
  }

  async function openSource(kind: "Resume" | "Vacancy") {
    if (!review) return;
    const preview = kind === "Resume" ? review.resume : review.vacancy;
    setSource({ kind, title: preview.title, text: "" });
    setSourceLoading(true);
    try {
      setSource(await api<ReviewSource>(`/api/v1/reviews/${id}/source/${kind}`));
    } catch (e) {
      setSource({ kind, title: preview.title, text: messageOf(e) });
    } finally {
      setSourceLoading(false);
    }
  }

  if (!review) return <FullScreenLoader contained />;
  const running = ["Queued", "Running"].includes(review.status);
  const initialEvaluations = review.evaluations.filter((evaluation) => evaluation.phase !== "Verification");
  const verificationEvaluations = review.evaluations.filter((evaluation) => evaluation.phase === "Verification");
  return (
    <div className="app-page result-page">
      <div className="page-title result-title">
        <div className="review-title-pair">
          <span>{reviewModeLabel(review.mode)}</span>
          <div><small>Резюме</small><h1>{review.resume.title}</h1></div>
          <div><small>Вакансия</small><h2>{review.vacancy.title}</h2></div>
          <p>{formatDate(review.createdAt)}</p>
        </div>
        <Status status={review.status} />
      </div>
      <section className="source-preview-grid" aria-label="Сравниваемые документы">
        <SourcePreviewCard kind="Resume" document={review.resume} onOpen={() => void openSource("Resume")} />
        <SourcePreviewCard kind="Vacancy" document={review.vacancy} onOpen={() => void openSource("Vacancy")} />
      </section>
      {running && <div className="running-banner ai-generation"><RefreshCw className="spin" /><div><strong>{review.mode === "Turnkey" ? turnkeyStageLabel(review.turnkeyStage) : "Модели оценивают документы"}</strong><p>Получено {review.successfulModelCount} из {review.requestedModelCount} оценок. Страница обновится автоматически.</p></div></div>}
      {!running && review.rewriteComparison && <RewriteComparisonPanel comparison={review.rewriteComparison} />}
      {!running && (
        <>
          {review.mode === "Turnkey" && (
            <section className="turnkey-outcome panel">
              <div><span>До доработки</span><strong>{review.baselineScore == null ? "—" : Math.round(review.baselineScore)}</strong></div>
              <TrendingUp />
              <div><span>После доработки</span><strong>{review.verificationScore == null ? "—" : Math.round(review.verificationScore)}</strong></div>
              <div className="turnkey-growth">
                <span>Эффект</span>
                <strong>{review.scoreGrowth == null ? "Без автодоработки" : `${review.scoreGrowth >= 0 ? "+" : ""}${formatScore(review.scoreGrowth)} п.`}</strong>
                <small>{turnkeyOutcomeCopy(review)}</small>
              </div>
              <div className="turnkey-price"><span>Итог</span><strong>{review.finalPriceTokens} токенов</strong></div>
            </section>
          )}
          <section className="result-hero">
            <div className="result-score"><span>{review.mode === "Turnkey" && review.verificationScore != null ? "Индекс после доработки" : "Медианный индекс"}</span><strong>{review.aggregateScore == null ? "—" : Math.round(review.aggregateScore)}<small>/100</small></strong><p>Диапазон {review.minimumScore ?? "—"}–{review.maximumScore ?? "—"}</p></div>
            <div className="result-explain"><span>Что это значит</span><h2>{scoreHeadline(review.aggregateScore)}</h2><p>Получено {review.successfulModelCount} из {review.requestedModelCount} независимых оценок. Сервис пересчитал каждую по рубрике v1.</p></div>
          </section>
        </>
      )}
      {error && <ErrorBox>{error}</ErrorBox>}
      {review.mode === "Turnkey" && <div className="phase-heading"><span>Первичный прогон</span><h2>Как модели оценили исходное резюме</h2></div>}
      <div className="evaluation-grid">
        {initialEvaluations.map((evaluation) => <EvaluationCard key={evaluation.id} evaluation={evaluation} recommendationsLocked={review.recommendationsAccess === "Locked"} onUnlock={() => void unlock()} unlocking={busy === "unlock"} />)}
      </div>
      {verificationEvaluations.length > 0 && (
        <>
          <div className="phase-heading verification"><span>Контрольный прогон</span><h2>Что изменилось после автодоработки</h2></div>
          <div className="evaluation-grid">
            {verificationEvaluations.map((evaluation) => <EvaluationCard key={evaluation.id} evaluation={evaluation} recommendationsLocked={false} />)}
          </div>
        </>
      )}
      {!running && (
        <section className="result-actions panel">
          <div><h2>Следующий шаг</h2><p>Откройте рекомендации, получите ещё одну версию или добавьте взгляд модели.</p></div>
          <div className="action-row">
            {review.recommendationsAccess === "Locked" && <button className="button secondary" onClick={unlock} disabled={!!busy}><LockKeyhole /> {busy === "unlock" ? "Открываем…" : "Открыть рекомендации · 5"}</button>}
            {review.mode !== "Turnkey" && <div className="select-action"><select value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)}>{models.filter((model) => !review.evaluations.some((evaluation) => evaluation.modelId === model.modelId)).map((model) => <option value={model.modelId} key={model.modelId}>{model.displayName} · {model.tokenPrice}</option>)}</select><button className="button secondary" disabled={!selectedModel || !!busy} onClick={addModel}>Добавить модель</button></div>}
            {review.rewrites[0] && <button className="button secondary" onClick={() => void openRewrite(review.rewrites[0].id)}><Eye /> Открыть улучшенную версию</button>}
            <button className="button ai-action" onClick={() => void createRewrite(false)} disabled={!!busy}><WandSparkles className="ai-marker" /> {busy === "rewrite" ? "Создаём…" : review.rewrites.length ? "Ещё версия · 30" : "Переписать · 30"}</button>
            <button className="button primary" onClick={() => void createRewrite(true)} disabled={!!busy}><FileCheck2 /> {busy === "rewrite-and-review" ? "Создаём и проверяем…" : "Переписать и проверить соответствие · 75"}</button>
          </div>
        </section>
      )}
      {rewrite && rewriteOpen && (
        <RewriteModal
          rewrite={rewrite}
          versions={review.rewrites}
          originalText={rewriteOriginal}
          draft={rewriteDraft || rewrite.content || ""}
          savedContent={rewriteSavedContent}
          answers={rewriteAnswers}
          busy={busy}
          notice={rewriteNotice}
          error={error}
          onClose={() => setRewriteOpen(false)}
          onSelectVersion={(rewriteId) => void openRewrite(rewriteId)}
          onDraftChange={(value) => { setRewriteDraft(value); setRewriteNotice(null); }}
          onAnswerChange={(index, value) => setRewriteAnswers((current) => current.map((item, itemIndex) => itemIndex === index ? value : item))}
          onSubmitAnswers={() => void submitRewriteAnswers()}
          onSave={() => void saveRewrite()}
          onVerify={() => void verifyImprovedResume()}
        />
      )}
      {source && (
        <DocumentModal
          source={source}
          loading={sourceLoading}
          onClose={() => setSource(null)}
        />
      )}
    </div>
  );
}

function SourcePreviewCard({ kind, document, onOpen }: { kind: "Resume" | "Vacancy"; document: DocumentPreview; onOpen: () => void }) {
  return (
    <article className="source-preview-card">
      <div><span>{kind === "Resume" ? "Резюме" : "Вакансия"}</span><strong>{document.title}</strong></div>
      <p>{document.preview}</p>
      <button className="text-button" onClick={onOpen}><Eye /> Открыть полностью</button>
    </article>
  );
}

function DocumentModal({ source, loading, onClose }: { source: ReviewSource; loading: boolean; onClose: () => void }) {
  useEffect(() => {
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="document-modal" role="dialog" aria-modal="true" aria-labelledby="document-modal-title">
        <header>
          <div><span>{source.kind === "Resume" ? "Резюме" : "Вакансия"}</span><h2 id="document-modal-title">{source.title}</h2></div>
          <button className="icon-button" aria-label="Закрыть документ" onClick={onClose}><X /></button>
        </header>
        {loading ? <InlineLoader /> : <pre>{source.text}</pre>}
      </section>
    </div>
  );
}

function EvaluationCard({
  evaluation,
  recommendationsLocked,
  onUnlock,
  unlocking = false
}: {
  evaluation: Evaluation;
  recommendationsLocked: boolean;
  onUnlock?: () => void;
  unlocking?: boolean;
}) {
  const criteria = normalizeCriteria(evaluation.criteria);
  const evidence = normalizeTextItems(evaluation.evidence);
  const gaps = normalizeTextItems(evaluation.gaps);
  const recommendations = normalizeTextItems(evaluation.recommendations);
  return (
    <article className="evaluation-card">
      <div className="evaluation-head"><div><span>{evaluation.provider ?? "LLM"}</span><h2>{evaluation.displayName}</h2><small>{evaluation.modelId}</small></div><div className="model-score">{evaluation.totalScore == null ? "—" : Math.round(evaluation.totalScore)}</div></div>
      {evaluation.status === "Failed" ? <div className="evaluation-failed"><CircleAlert /> Модель не ответила. Списание будет возвращено по правилам режима.</div> : (
        <>
          <div className="criteria-list">
            {criteria.map((criterion, index) => {
              const maximum = criteriaMaxima[criterion.key] ?? Math.max(1, criterion.score);
              const width = Math.min(100, Math.max(0, (criterion.score / maximum) * 100));
              return (
                <div className="criterion-row" key={`${criterion.key}-${index}`}>
                  <div className="criterion-main">
                    <span>{criteriaLabels[criterion.key] ?? criterion.key}</span>
                    <div><i style={{ width: `${width}%` }} /></div>
                    <strong>{formatScore(criterion.score)}/{maximum}</strong>
                  </div>
                  {!recommendationsLocked && criterion.explanation && <p>{criterion.explanation}</p>}
                </div>
              );
            })}
          </div>
          {recommendationsLocked ? (
            <div className="locked-analysis">
              <div className="locked-analysis-title"><LockKeyhole /><div><strong>Детальный разбор скрыт</strong><span>Откройте один раз — для всех моделей этого анализа.</span></div></div>
              <ul><li>Пояснения к баллам</li><li>На что опирается оценка</li><li>Что снижает индекс</li><li>Рекомендации</li></ul>
              <button className="button secondary full" onClick={onUnlock} disabled={unlocking || !onUnlock}><LockKeyhole /> {unlocking ? "Открываем…" : "Открыть рекомендации · 5"}</button>
            </div>
          ) : (
            <>
              {evidence.length > 0 && <div className="evaluation-evidence"><h3>На что опирается оценка</h3><ul>{evidence.slice(0, 4).map((item, index) => <li key={`${index}-${item}`}>{item}</li>)}</ul></div>}
              {gaps.length > 0 && <div className="gaps"><h3>Что снижает индекс</h3><ul>{gaps.slice(0, 4).map((gap, index) => <li key={`${index}-${gap}`}>{gap}</li>)}</ul></div>}
              {recommendations.length > 0 && <div className="recommendations"><h3>Как улучшить</h3><ol>{recommendations.map((item, index) => <li key={`${index}-${item}`}>{item}</li>)}</ol></div>}
            </>
          )}
        </>
      )}
    </article>
  );
}

function RewriteComparisonPanel({ comparison }: { comparison: RewriteComparison }) {
  const deltaText = (value: number | null) => value == null ? "—" : `${value > 0 ? "+" : ""}${formatScore(value)}`;
  const deltaClass = (value: number | null) => value == null || value === 0 ? "neutral" : value > 0 ? "positive" : "negative";
  const regressions = comparison.models.filter((model) => model.delta != null && model.delta < 0).length;
  return (
    <section className="rewrite-comparison panel" aria-labelledby="rewrite-comparison-title">
      <div className="rewrite-comparison-head">
        <div><span>Результат доработки</span><h2 id="rewrite-comparison-title">Сравнение с исходным резюме</h2><p>Медианный индекс и баллы совпадающих моделей до и после по той же вакансии.</p></div>
        <Link className="button secondary" to={`/app/reviews/${comparison.sourceReviewId}`}>Открыть исходный анализ</Link>
      </div>
      <div className="rewrite-comparison-summary">
        <div><span>До</span><strong>{comparison.beforeScore == null ? "—" : formatScore(comparison.beforeScore)}</strong></div>
        <ArrowRight />
        <div><span>После</span><strong>{comparison.afterScore == null ? "—" : formatScore(comparison.afterScore)}</strong></div>
        <div className={`comparison-delta ${deltaClass(comparison.delta)}`}><span>Изменение</span><strong>{deltaText(comparison.delta)}</strong></div>
      </div>
      <div className="comparison-table-wrap">
        <table className="comparison-table">
          <thead><tr><th>Модель</th><th>До</th><th>После</th><th>Разница</th></tr></thead>
          <tbody>
            {comparison.models.map((model) => (
              <tr key={model.modelId}>
                <td>{model.displayName}</td>
                <td>{model.beforeScore == null ? "—" : formatScore(model.beforeScore)}</td>
                <td>{formatScore(model.afterScore)}</td>
                <td className={deltaClass(model.delta)}>{deltaText(model.delta)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {regressions > 0 && <div className="comparison-warning"><CircleAlert /> У {regressions} {regressions === 1 ? "модели оценка снизилась" : "моделей оценка снизилась"}. Это видно отдельно, а не скрыто средним баллом.</div>}
    </section>
  );
}

function RewriteModal({
  rewrite,
  versions,
  originalText,
  draft,
  savedContent,
  answers,
  busy,
  notice,
  error,
  onClose,
  onSelectVersion,
  onDraftChange,
  onAnswerChange,
  onSubmitAnswers,
  onSave,
  onVerify
}: {
  rewrite: RewriteDetails;
  versions: RewriteSummary[];
  originalText: string;
  draft: string;
  savedContent: string;
  answers: string[];
  busy: string | null;
  notice: string | null;
  error: string | null;
  onClose: () => void;
  onSelectVersion: (id: string) => void;
  onDraftChange: (value: string) => void;
  onAnswerChange: (index: number, value: string) => void;
  onSubmitAnswers: () => void;
  onSave: () => void;
  onVerify: () => void;
}) {
  const [editing, setEditing] = useState(false);
  useEffect(() => setEditing(false), [rewrite.id]);
  useEffect(() => {
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);
  const unanswered = (rewrite.questions ?? []).some((_, index) => !answers[index]?.trim());
  return (
    <div className="rewrite-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="rewrite-modal" role="dialog" aria-modal="true" aria-labelledby="rewrite-modal-title">
        <header>
          <div><span>Доработка резюме · версия {rewrite.version}</span><h2 id="rewrite-modal-title"><WandSparkles /> Резюме под вакансию</h2><p>Claude Opus получает вакансию и полный фидбэк всех моделей.</p></div>
          <div className="rewrite-modal-header-actions">
            {versions.length > 1 && <select aria-label="Версия резюме" value={rewrite.id} onChange={(event) => onSelectVersion(event.target.value)}>{versions.map((item) => <option key={item.id} value={item.id}>Версия {item.version} · {item.status}</option>)}</select>}
            <button className="icon-button" aria-label="Закрыть доработку" onClick={onClose}><X /></button>
          </div>
        </header>
        <div className="rewrite-modal-body">
          {notice && <div className="success-box"><Check /> {notice}</div>}
          {error && <ErrorBox>{error}</ErrorBox>}
          {rewrite.status === "AwaitingInput" && (
            <section className="rewrite-questions">
              <div><span>Нужны подтверждённые факты</span><h3>Уточните детали перед правкой</h3><p>Модель спрашивает только то, чего нет в исходном резюме, но что нужно для выполнения рекомендаций без выдумывания.</p></div>
              {(rewrite.questions ?? []).map((question, index) => (
                <label key={`${index}-${question}`}>{question}<textarea maxLength={2000} value={answers[index] ?? ""} onChange={(event) => onAnswerChange(index, event.target.value)} placeholder="Ваш ответ…" /></label>
              ))}
              <button className="button primary large" disabled={!!busy || unanswered} onClick={onSubmitAnswers}>{busy === "rewrite-answers" ? "Продолжаем…" : "Передать ответы и продолжить"}</button>
            </section>
          )}
          {["Queued", "Running"].includes(rewrite.status) && <div className="rewrite-progress"><InlineLoader /><h3>Модель анализирует все замечания</h3><p>Сначала она проверит, нужны ли уточнения, затем подготовит полную версию. Окно можно закрыть — процесс продолжится.</p></div>}
          {rewrite.status === "Failed" && <div className="evaluation-failed"><CircleAlert /> {rewrite.failureReason ?? (rewrite.isTurnkey ? "Автодоработка не завершилась. Пакет пересчитан по базовой цене." : `Не удалось получить полную версию. ${rewrite.verifyAfterRewrite ? 75 : 30} токенов возвращены.`)}</div>}
          {rewrite.status === "Completed" && draft && (
            <>
              {rewrite.failureReason && <div className="evaluation-failed"><CircleAlert /> {rewrite.failureReason}</div>}
              <div className="rewrite-toolbar">
                <button className="button secondary" onClick={() => setEditing((value) => !value)}>{editing ? <><Eye /> Посмотреть изменения</> : <>Редактировать текст</>}</button>
                {editing && <button className="button secondary" onClick={onSave} disabled={!!busy || draft === savedContent}>{busy === "rewrite-save" ? "Сохраняем…" : "Сохранить правки"}</button>}
                {rewrite.docxUrl && <a className="button success-action" href={apiFileUrl(new URL(rewrite.docxUrl).pathname)}><FileCheck2 /> Скачать DOCX</a>}
                {rewrite.verificationReviewId && <Link className="button primary" to={`/app/reviews/${rewrite.verificationReviewId}`}><FileCheck2 /> Открыть проверку</Link>}
                {!rewrite.verificationReviewId && (!rewrite.verifyAfterRewrite || !!rewrite.failureReason) && <button className="button primary" onClick={onVerify} disabled={!!busy || draft !== savedContent} title={draft !== savedContent ? "Сначала сохраните правки" : undefined}><FileCheck2 /> {busy === "rewrite-check" ? "Запускаем проверку…" : "Проверить улучшенное резюме · 50"}</button>}
              </div>
              {editing ? <textarea className="rewrite-plain-editor" aria-label="Текст улучшенного резюме" maxLength={50000} value={draft} onChange={(event) => onDraftChange(event.target.value)} /> : <><div className="rewrite-change-note"><span /> Подсвечены строки, которых не было в исходном резюме.</div><PlainTextDiff originalText={originalText} value={draft} /></>}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function PlainTextDiff({ originalText, value }: { originalText: string; value: string }) {
  const originalLines = new Set(originalText.replace(/\r\n?/g, "\n").split("\n").map((line) => line.trim()).filter(Boolean));
  const lines = value.replace(/\r\n?/g, "\n").split("\n");
  return <pre className="rewrite-plain-text">{lines.map((line, index) => <span className={line.trim() && !originalLines.has(line.trim()) ? "changed-line" : undefined} key={`${index}-${line}`}>{line || " "}{index < lines.length - 1 ? "\n" : ""}</span>)}</pre>;
}

function Status({ status }: { status: string }) {
  const labels: Record<string, string> = { Queued: "В очереди", Running: "В работе", AwaitingInput: "Нужны уточнения", Completed: "Готово", Partial: "Частично", Failed: "Ошибка" };
  return <span className={`status status-${status.toLowerCase()}`}>{labels[status] ?? status}</span>;
}

function ErrorBox({ children }: { children: ReactNode }) {
  return <div className="error-box"><CircleAlert />{children}</div>;
}

function InlineLoader() {
  return <div className="inline-loader"><RefreshCw className="spin" /> Загружаем…</div>;
}

function FullScreenLoader({ contained = false }: { contained?: boolean }) {
  return <div className={contained ? "full-loader contained" : "full-loader"}><span className="brand-mark">CV</span><RefreshCw className="spin" /></div>;
}

function messageOf(error: unknown): string {
  if (error instanceof ApiError) {
    const details = error.details
      ? [...new Set(Object.values(error.details).flat().filter(Boolean))]
      : [];
    if (details.length) return details.join(" ");
    if (error.status === 429) return "Слишком много попыток. Подождите минуту и попробуйте снова.";
    return error.message || "Запрос не выполнен. Попробуйте ещё раз.";
  }
  if (error instanceof Error) return error.message;
  return "Что-то пошло не так. Попробуйте ещё раз.";
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function registrationPasswordError(
  password: string,
  confirmation: string
): string | null {
  if (!password) return "Введите пароль.";
  if (
    password.length < 10 ||
    !/\p{Lu}/u.test(password) ||
    !/\p{Ll}/u.test(password) ||
    !/\p{Nd}/u.test(password)
  ) {
    return "Пароль должен содержать не менее 10 символов, хотя бы одну заглавную букву, одну строчную букву и одну цифру.";
  }
  if (!confirmation) return "Повторите пароль.";
  return password === confirmation ? null : "Пароли не совпадают.";
}

function normalizeCriteria(criteria: Evaluation["criteria"]): CriterionScore[] {
  if (!criteria) return [];
  const values: Array<[string | undefined, unknown]> = Array.isArray(criteria)
    ? criteria.map((item) => [undefined, item])
    : Object.entries(criteria);

  return values.flatMap(([fallbackKey, value]) => {
    if (typeof value === "number" && fallbackKey) {
      return Number.isFinite(value) ? [{ key: fallbackKey, score: value }] : [];
    }
    if (!value || typeof value !== "object") return [];

    const candidate = value as Partial<CriterionScore>;
    const key = typeof candidate.key === "string" && candidate.key.trim()
      ? candidate.key
      : fallbackKey;
    const score = Number(candidate.score);
    if (!key || !Number.isFinite(score)) return [];
    return [{
      key,
      score,
      explanation: typeof candidate.explanation === "string"
        ? candidate.explanation.trim()
        : undefined
    }];
  });
}

function normalizeTextItems(items: unknown): string[] {
  if (!Array.isArray(items)) return [];
  return items
    .filter((item): item is string => typeof item === "string")
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatScore(score: number): string {
  return Number.isInteger(score) ? String(score) : score.toFixed(1);
}

function sourceReady(source: InputSource): boolean {
  if (source.sourceType === "Text") return source.text.trim().length >= 80;
  if (source.sourceType === "Url") return /^https?:\/\//i.test(source.url);
  if (source.sourceType === "Saved" || source.sourceType === "Hh") return !!source.documentId;
  return !!source.file && source.file.size <= 10 * 1024 * 1024;
}

function reviewModeLabel(mode: string): string {
  if (mode === "Turnkey") return "Резюме под ключ";
  if (mode === "PremiumTen") return "Расширенный анализ";
  return "Бесплатный анализ";
}

function turnkeyStageLabel(stage?: string): string {
  if (stage === "Rewriting") return "AI автоматически применяет общие рекомендации";
  if (stage === "Verification") return "Модели проверяют улучшенную версию";
  return "Модели проводят первичный анализ";
}

function turnkeyOutcomeCopy(review: Review): string {
  if (review.turnkeyStage === "SkippedHighScore") {
    return "Стартовая оценка 85+: автодоработка не применялась.";
  }
  if (review.turnkeyStage === "RewriteFailed") {
    return "Доработка не завершилась — сохранена базовая цена.";
  }
  if (review.turnkeyStage === "VerificationFailed") {
    return "Эффект не подтверждён — сохранена базовая цена.";
  }
  if (review.scoreGrowth == null || review.requiredGrowth == null) {
    return "Контрольный результат недоступен.";
  }
  return review.scoreGrowth >= review.requiredGrowth
    ? `Целевой рост ${formatScore(review.requiredGrowth)} п. достигнут.`
    : `Рост ниже цели ${formatScore(review.requiredGrowth)} п. — цена снижена.`;
}

function scoreHeadline(score: number | null): string {
  if (score == null) return "Недостаточно данных для индекса";
  if (score >= 80) return "Сильное соответствие по большинству моделей";
  if (score >= 65) return "Хорошая база с заметными точками роста";
  if (score >= 45) return "Есть совпадения, но резюме стоит адаптировать";
  return "Модели видят существенный разрыв с вакансией";
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("ru-RU", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function delay(milliseconds: number) {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

export default App;
